import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { ArrowUp, ChevronDown, ChevronRight, Info, Loader2, MessageSquarePlus, MessagesSquare, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { api, del, get } from "@/lib/api";
import { errorMessage } from "@/lib/errors";
import { formatDateTime, formatRelative, formatUsd } from "@/lib/format";
import { companyMemorySourcePath } from "@/lib/moduleLinks";
import { cn } from "@/lib/utils";
import { PageHeader } from "@/components/common/PageHeader";
import { AgentChip } from "@/components/common/AgentChip";
import { CapabilityBadge, ModelChip, StatusBadge } from "@/components/common/CapabilityBadge";
import { LightMarkdown } from "@/components/common/LightMarkdown";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Progress } from "@/components/ui/progress";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

const POLL_MS = 3000;
const POLL_MAX_MS = 4 * 60 * 1000;
const SUGGESTION_KEYS = ["capabilities", "posts", "strategy", "icp"];

/* ---------------- Conversations list ---------------- */
const ConversationList = ({ conversations, loading, activeId, onSelect, onNew, onDelete }) => {
  const { t } = useTranslation();
  return (
    <div className="flex h-full flex-col" data-testid="assistant-conversations">
      <div className="flex items-center justify-between border-b border-border px-3 py-3">
        <span className="font-heading text-sm font-semibold">{t("assistant.conversations")}</span>
        <Button size="sm" variant="outline" className="bg-card" onClick={onNew} data-testid="assistant-new-conversation">
          <Plus className="mr-1.5 h-4 w-4" />{t("assistant.newConversation")}
        </Button>
      </div>
      <div className="og-scroll flex-1 overflow-y-auto p-2">
        {loading && [0, 1, 2].map((i) => <Skeleton key={i} className="mb-2 h-12 w-full" />)}
        {!loading && conversations.length === 0 && <p className="px-2 py-6 text-sm text-muted-foreground" data-testid="assistant-no-conversations">{t("assistant.noConversations")}</p>}
        {conversations.map((c) => (
          <div key={c.id} className={cn("group mb-1 flex items-start gap-2 rounded-lg border border-transparent px-2 py-2 transition-colors duration-150 hover:bg-muted", activeId === c.id && "border-border bg-accent")} data-testid={`assistant-conversation-${c.id}`}>
            <button type="button" onClick={() => onSelect(c.id)} className="og-focus min-w-0 flex-1 rounded text-left" data-testid={`assistant-conversation-open-${c.id}`}>
              <div className="truncate text-sm font-medium text-foreground">{c.title}</div>
              <div className="text-[11px] text-muted-foreground">{formatRelative(c.last_message_at || c.created_at)}</div>
            </button>
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button size="icon" variant="ghost" className="h-7 w-7 shrink-0 text-muted-foreground opacity-70 hover:text-destructive" aria-label={t("assistant.deleteConversation")} data-testid={`assistant-conversation-delete-${c.id}`}>
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent className="bg-card">
                <AlertDialogHeader>
                  <AlertDialogTitle>{t("assistant.deleteConversation")}</AlertDialogTitle>
                  <AlertDialogDescription>{t("common.confirmDelete")}</AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel data-testid="assistant-delete-cancel">{t("common.cancel")}</AlertDialogCancel>
                  <AlertDialogAction onClick={() => onDelete(c.id)} className="bg-destructive text-destructive-foreground hover:bg-destructive/90" data-testid="assistant-delete-confirm">{t("common.delete")}</AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </div>
        ))}
      </div>
    </div>
  );
};

/* ---------------- Delegation timeline ---------------- */
const StepCard = ({ step, index }) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(index === 0);
  const statusLabel = step.status === "planned" ? t("assistant.stepStatus.planned") : t(`assistant.stepStatus.${step.status}`, { defaultValue: step.status });
  return (
    <li className="relative pl-6" data-testid={`assistant-step-${index}`}>
      <span className="absolute left-0 top-1.5 flex h-4 w-4 items-center justify-center rounded-full border border-border bg-card text-[10px] font-semibold text-muted-foreground" aria-hidden="true">{index + 1}</span>
      <div className="flex flex-wrap items-center gap-2">
        <AgentChip agentId={step.agent_id} persona={step.persona} />
        <span className="font-mono text-[11px] text-muted-foreground">{step.task_type}</span>
        <StatusBadge status={step.status} label={statusLabel} testId={`assistant-step-status-${index}`} />
      </div>
      {step.reason && <p className="mt-1 text-xs text-muted-foreground">{step.reason}</p>}
      {step.summary && step.status !== "planned" && <p className="mt-1 text-sm">{step.summary}</p>}
      {step.error && <p className="mt-1 text-xs text-rose-800">{step.error}</p>}
      {step.preview && (
        <Collapsible open={open} onOpenChange={setOpen} className="mt-2">
          <CollapsibleTrigger asChild>
            <Button variant="outline" size="sm" className="h-7 bg-card text-xs" data-testid={`assistant-step-preview-toggle-${index}`}>
              {open ? <ChevronDown className="mr-1 h-3.5 w-3.5" /> : <ChevronRight className="mr-1 h-3.5 w-3.5" />}
              {open ? t("assistant.hidePreview") : t("assistant.showPreview")}
            </Button>
          </CollapsibleTrigger>
          <CollapsibleContent>
            <div className="mt-2 rounded-lg border border-dashed border-border bg-background p-3" data-testid={`assistant-step-preview-${index}`}>
              <div className="og-section-label mb-2">{t("assistant.preview")}</div>
              <LightMarkdown text={step.preview} />
            </div>
          </CollapsibleContent>
        </Collapsible>
      )}
    </li>
  );
};

/* ---------------- Message bubbles ---------------- */
export const AssistantMessage = ({ message }) => {
  const { t } = useTranslation();
  const meta = message.metadata || {};
  const calls = meta.routing?.calls || [];
  const steps = meta.steps || [];
  const failed = meta.status === "failed";
  const sourceCandidates = meta.grounding?.company_memory_used === true && Array.isArray(meta.sources)
    ? meta.sources
    : [];
  const sources = sourceCandidates
    .filter((source) => (
      source !== null
      && typeof source === "object"
      && !Array.isArray(source)
      && typeof source.citation === "string"
      && /^S[1-8]$/.test(source.citation)
    ))
    .slice(0, 8);
  return (
    <div className="flex gap-3" data-testid={`assistant-message-${message.id}`}>
      <div className="mt-1 hidden h-7 w-7 shrink-0 items-center justify-center rounded-md border border-slate-200 bg-slate-100 font-heading text-[10px] font-semibold text-slate-900 sm:flex" aria-hidden="true">OG</div>
      <div className="min-w-0 flex-1 space-y-3">
        <div className={cn("og-card p-4", failed && "border-rose-200")}>
          {failed && <p className="mb-2 text-xs font-medium text-rose-800" data-testid="assistant-message-failed">{t("assistant.failedHint")}</p>}
          <LightMarkdown text={message.content} testId="assistant-message-content" />
          {sources.length > 0 && (
            <section className="mt-4 border-t border-border pt-3" aria-label={t("assistant.sources.title")} data-testid="assistant-message-sources">
              <h3 className="mb-2 text-xs font-semibold text-muted-foreground">{t("assistant.sources.title")}</h3>
              <ul className="space-y-2">
                {sources.map((source, index) => {
                  const translatedSourceType = typeof source.source_type === "string" && source.source_type.length > 0
                    ? t(`assistant.sources.types.${source.source_type}`, { defaultValue: source.source_type })
                    : null;
                  const sourceType = typeof translatedSourceType === "string" && translatedSourceType.length > 0
                    ? translatedSourceType
                    : typeof source.source_type === "string" && source.source_type.length > 0
                      ? source.source_type
                      : "—";
                  const title = typeof source.title === "string" ? source.title : "—";
                  const occurredAt = typeof source.occurred_at === "string" && source.occurred_at.length > 0
                    ? formatDateTime(source.occurred_at)
                    : null;
                  const path = companyMemorySourcePath(source);
                  return (
                    <li key={`${source.citation}-${index}`} className="flex flex-wrap items-start justify-between gap-x-4 gap-y-1 rounded-md bg-muted/40 px-2.5 py-2 text-sm">
                      <div className="min-w-0 flex-1">
                        <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
                          <span className="shrink-0 font-mono text-[11px] font-medium text-muted-foreground">[{source.citation}]</span>
                          <span className="text-xs text-muted-foreground">{sourceType}</span>
                          <span className="min-w-0 break-words font-medium text-foreground">{title}</span>
                        </div>
                        {occurredAt && <p className="mt-0.5 pl-8 text-[11px] text-muted-foreground">{occurredAt}</p>}
                      </div>
                      {typeof path === "string" && path.startsWith("/") && (
                        <Link to={path} className="shrink-0 text-xs font-medium text-primary underline-offset-4 hover:underline">
                          {t("assistant.sources.open")}
                        </Link>
                      )}
                    </li>
                  );
                })}
              </ul>
            </section>
          )}
          {(meta.intent || calls.length > 0) && (
            <div className="mt-3 flex flex-wrap items-center gap-1.5 border-t border-border pt-3 text-[11px] text-muted-foreground" data-testid="assistant-message-meta">
              {meta.intent && <Badge variant="outline" className="border-border bg-muted text-foreground">{t(`assistant.intent.${meta.intent}`, { defaultValue: meta.intent })}</Badge>}
              {meta.intent === "chat" && meta.depth && <Badge variant="outline" className="border-border bg-card">{t(`assistant.depth.${meta.depth}`, { defaultValue: meta.depth })}</Badge>}
              {meta.intent === "delegate" && meta.complexity && <Badge variant="outline" className="border-border bg-card">{t(`assistant.complexity.${meta.complexity}`, { defaultValue: meta.complexity })}</Badge>}
              {calls.map((c, i) => (
                <span key={i} className="inline-flex items-center gap-1" data-testid={`assistant-routing-call-${i}`}>
                  <CapabilityBadge capability={c.capability} />
                  <ModelChip model={c.model} latencyMs={c.latency_ms} fallback={c.fallback_used} />
                </span>
              ))}
              {typeof meta.tokens === "number" && <span className="ml-auto font-mono" data-testid="assistant-message-tokens">{t("assistant.tokensUsed", { tokens: meta.tokens, cost: formatUsd(meta.cost_usd, 4) })}</span>}
            </div>
          )}
        </div>
        {steps.length > 0 && (
          <div className="og-card p-4" data-testid="assistant-delegation-timeline">
            <div className="mb-3 flex items-center justify-between">
              <span className="font-heading text-sm font-semibold">{t("assistant.delegationPlan")}</span>
              {message.agent_run_id && <Button asChild variant="link" size="sm" className="h-auto p-0 text-xs"><Link to={`/app/ai?tab=runs&run=${message.agent_run_id}`} data-testid="assistant-open-run">{t("assistant.runDetails")}</Link></Button>}
            </div>
            <ol className="space-y-4 border-l border-border pl-0 [&>li]:ml-2">
              {steps.map((s, i) => <StepCard key={i} step={s} index={i} />)}
            </ol>
          </div>
        )}
      </div>
    </div>
  );
};

const UserMessage = ({ message }) => {
  const { t } = useTranslation();
  return (
    <div className="flex justify-end" data-testid={`user-message-${message.id}`}>
      <div className="max-w-[85%] rounded-xl bg-slate-900 px-4 py-3 text-sm text-white">
        <div className="mb-1 text-[10px] uppercase tracking-wide text-slate-300">{t("assistant.you")}</div>
        <p className="whitespace-pre-wrap">{message.content}</p>
      </div>
    </div>
  );
};

const WorkingBubble = ({ status }) => {
  const { t } = useTranslation();
  const [value, setValue] = useState(8);
  useEffect(() => {
    const id = setInterval(() => setValue((v) => Math.min(92, v + 3)), 1200);
    return () => clearInterval(id);
  }, []);
  return (
    <div className="flex gap-3" data-testid="assistant-working">
      <div className="mt-1 hidden h-7 w-7 shrink-0 items-center justify-center rounded-md border border-slate-200 bg-slate-100 font-heading text-[10px] font-semibold text-slate-900 sm:flex" aria-hidden="true">OG</div>
      <div className="og-card flex-1 p-4">
        <div className="flex items-center gap-2 text-sm font-medium"><Loader2 className="h-4 w-4 animate-spin text-primary" aria-hidden="true" />{status === "queued" ? t("assistant.queued") : t("assistant.running")}</div>
        <p className="mt-1 text-xs text-muted-foreground">{t("assistant.longRunningHint")}</p>
        <Progress value={value} className="mt-3 h-1.5" />
        <div className="mt-3 space-y-2"><Skeleton className="h-3 w-3/4" /><Skeleton className="h-3 w-1/2" /></div>
      </div>
    </div>
  );
};

/* ---------------- Page ---------------- */
export default function AssistantPage() {
  const { t } = useTranslation();
  const { orgId } = useAuth();
  const queryClient = useQueryClient();
  const [activeId, setActiveId] = useState(null);
  const [draft, setDraft] = useState("");
  const [pending, setPending] = useState(null); // { userMessage, runId, status, conversationId }
  const [mobileListOpen, setMobileListOpen] = useState(false);
  const bottomRef = useRef(null);
  const pollRef = useRef(null);

  const { data: policies, isLoading: policiesLoading } = useQuery({ queryKey: ["ai-policies", orgId], queryFn: () => get("/ai/policies"), enabled: !!orgId, staleTime: 60_000 });
  const { data: conversations = [], isLoading: convLoading } = useQuery({ queryKey: ["conversations", orgId], queryFn: () => get("/assistant/conversations"), enabled: !!orgId });
  const { data: conversation, isLoading: msgLoading } = useQuery({ queryKey: ["conversation", orgId, activeId], queryFn: () => get(`/assistant/conversations/${activeId}`), enabled: !!orgId && !!activeId });

  const messages = useMemo(() => conversation?.messages || [], [conversation]);

  useEffect(() => () => { if (pollRef.current) clearTimeout(pollRef.current); }, []);
  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: "smooth", block: "end" }); }, [messages.length, pending?.status]);

  const finishTurn = useCallback((convId) => {
    setPending(null);
    setActiveId(convId);
    queryClient.invalidateQueries({ queryKey: ["conversation", orgId, convId] });
    queryClient.invalidateQueries({ queryKey: ["conversations", orgId] });
    queryClient.invalidateQueries({ queryKey: ["ai-usage"] });
  }, [orgId, queryClient]);

  const poll = useCallback((runId, convId, startedAt) => {
    pollRef.current = setTimeout(async () => {
      try {
        const job = await get(`/assistant/jobs/${runId}`);
        if (job.status === "succeeded" || job.status === "failed") {
          finishTurn(convId);
          return;
        }
        setPending((p) => (p ? { ...p, status: job.status } : p));
      } catch (err) {
        if (err?.status === 404) { finishTurn(convId); return; }
      }
      if (Date.now() - startedAt > POLL_MAX_MS) {
        toast.error(t("assistant.pollError"));
        finishTurn(convId);
        return;
      }
      poll(runId, convId, startedAt);
    }, POLL_MS);
  }, [finishTurn, t]);

  const send = useMutation({
    mutationFn: async (text) => {
      const res = await api.post("/assistant/chat", { message: text, conversation_id: activeId || undefined }, { params: { wait_s: 25 } });
      return { status: res.status, data: res.data };
    },
    onMutate: (text) => {
      setPending({ userMessage: { id: `tmp-${Date.now()}`, role: "user", content: text }, status: "queued", conversationId: activeId });
      setDraft("");
    },
    onSuccess: ({ status, data }) => {
      const convId = data.conversation_id;
      if (status === 202 || !data.message) {
        setPending((p) => ({ ...(p || {}), runId: data.run_id, status: data.status || "running", conversationId: convId }));
        if (!activeId) setActiveId(convId);
        poll(data.run_id, convId, Date.now());
        return;
      }
      finishTurn(convId);
    },
    onError: (err) => {
      setPending(null);
      toast.error(errorMessage(err, "assistant.sendError"));
    },
  });

  const remove = useMutation({
    mutationFn: (id) => del(`/assistant/conversations/${id}`),
    onSuccess: (_, id) => {
      if (activeId === id) setActiveId(null);
      queryClient.invalidateQueries({ queryKey: ["conversations", orgId] });
      toast.success(t("assistant.conversationDeleted"));
    },
    onError: (err) => toast.error(errorMessage(err)),
  });

  const submit = (text) => {
    const value = (text ?? draft).trim();
    if (!value || send.isPending || pending) return;
    send.mutate(value);
  };
  const onKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      submit();
    }
  };
  const startNew = () => { setActiveId(null); setPending(null); setMobileListOpen(false); };
  const busy = !!pending;
  const showEmpty = !activeId && !busy;

  const listProps = { conversations, loading: convLoading, activeId, onSelect: (id) => { setActiveId(id); setMobileListOpen(false); }, onNew: startNew, onDelete: (id) => remove.mutate(id) };

  return (
    <div className="flex h-[calc(100vh-7.5rem)] min-h-[560px] flex-col gap-4" data-testid="assistant-page">
      <PageHeader
        title={t("assistant.title")}
        subtitle={t("assistant.subtitle")}
        testId="assistant-header"
        actions={
          <div className="flex items-center gap-2">
            <Sheet open={mobileListOpen} onOpenChange={setMobileListOpen}>
              <SheetTrigger asChild>
                <Button variant="outline" size="sm" className="bg-card lg:hidden" data-testid="assistant-open-conversations"><MessagesSquare className="mr-2 h-4 w-4" />{t("assistant.conversations")}</Button>
              </SheetTrigger>
              <SheetContent side="left" className="w-[320px] bg-card p-0">
                <SheetHeader className="sr-only"><SheetTitle>{t("assistant.conversations")}</SheetTitle></SheetHeader>
                <ConversationList {...listProps} />
              </SheetContent>
            </Sheet>
            <Button size="sm" onClick={startNew} className="hidden lg:inline-flex" data-testid="assistant-new-conversation-header"><MessageSquarePlus className="mr-2 h-4 w-4" />{t("assistant.newConversation")}</Button>
          </div>
        }
      />

      {/* Step 2.1 runs in plan_only mode: the banner is rendered immediately (while policies load)
          and only hidden once the backend explicitly reports another delegation mode. */}
      {(policiesLoading || !policies || policies.delegation_mode === "plan_only") && (
        <div className="flex items-start gap-2 rounded-lg border border-sky-200 bg-sky-50 px-3 py-2 text-xs text-sky-900" data-testid="assistant-plan-only-banner">
          <Info className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden="true" />
          <span>{t("assistant.planOnlyBanner")}</span>
        </div>
      )}

      <div className="grid min-h-0 flex-1 grid-cols-1 gap-4 lg:grid-cols-12">
        <aside className="og-card hidden min-h-0 lg:col-span-3 lg:block"><ConversationList {...listProps} /></aside>

        <section className="og-card flex min-h-0 flex-col lg:col-span-9" data-testid="assistant-thread">
          <div className="og-scroll flex-1 overflow-y-auto p-4 md:p-6">
            {activeId && msgLoading && <div className="space-y-4"><Skeleton className="ml-auto h-14 w-2/3" /><Skeleton className="h-24 w-full" /></div>}
            {showEmpty && (
              <div className="mx-auto max-w-2xl py-8" data-testid="assistant-empty">
                <h2 className="font-heading text-lg font-semibold">{t("assistant.emptyTitle")}</h2>
                <p className="mt-1 text-sm text-muted-foreground">{t("assistant.emptyHint")}</p>
                <div className="og-section-label mt-6 mb-2">{t("assistant.suggestions")}</div>
                <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                  {SUGGESTION_KEYS.map((k) => (
                    <button key={k} type="button" onClick={() => submit(t(`assistant.suggestionItems.${k}`))} className="og-card og-focus flex items-center justify-between gap-3 p-3 text-left text-sm transition-colors duration-150 hover:bg-muted/40" data-testid={`assistant-suggestion-${k}`}>
                      <span>{t(`assistant.suggestionItems.${k}`)}</span>
                      <ArrowUp className="h-4 w-4 shrink-0 rotate-45 text-muted-foreground" aria-hidden="true" />
                    </button>
                  ))}
                </div>
              </div>
            )}
            <div className="space-y-5">
              {messages.map((m) => (m.role === "user" ? <UserMessage key={m.id} message={m} /> : <AssistantMessage key={m.id} message={m} />))}
              {pending && <UserMessage message={pending.userMessage} />}
              {pending && <WorkingBubble status={pending.status} />}
            </div>
            <div ref={bottomRef} />
          </div>

          <div className="border-t border-border p-3 md:p-4">
            <div className="flex items-end gap-2">
              <Textarea
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                onKeyDown={onKeyDown}
                placeholder={t("assistant.placeholder")}
                rows={2}
                className="min-h-[56px] resize-none bg-background"
                disabled={busy}
                data-testid="orchestrator-prompt-input"
              />
              <Button onClick={() => submit()} disabled={busy || !draft.trim()} className="h-[56px] px-4" data-testid="orchestrator-prompt-submit" aria-label={t("assistant.send")}>
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <ArrowUp className="h-4 w-4" />}
              </Button>
            </div>
            <p className="mt-1.5 text-[11px] text-muted-foreground">{t("assistant.sendHint")}</p>
          </div>
        </section>
      </div>
    </div>
  );
}
